import { useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import Navbar from "../components/Navbar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Key, Mail, ArrowLeft, Check } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");
  
  // Request form (no token)
  const [email, setEmail] = useState("");
  const [requestSent, setRequestSent] = useState(false);
  
  // Reset form (with token)
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [resetComplete, setResetComplete] = useState(false);
  
  const [loading, setLoading] = useState(false);

  const [emailSentSuccess, setEmailSentSuccess] = useState(false);
  const [emailError, setEmailError] = useState("");
  
  const handleRequestReset = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error("INTRODUCE TU EMAIL");
      return;
    }
    
    setLoading(true);
    setEmailError("");
    try {
      const res = await axios.post(`${API}/auth/forgot-password`, { email });
      setRequestSent(true);
      
      if (res.data.email_sent) {
        setEmailSentSuccess(true);
        toast.success("EMAIL ENVIADO - REVISA TU BANDEJA DE ENTRADA");
      } else {
        setEmailSentSuccess(false);
        setEmailError(res.data.error || "No se pudo enviar el email");
        toast.error("NO SE PUDO ENVIAR EL EMAIL");
      }
    } catch (error) {
      toast.error("ERROR AL PROCESAR SOLICITUD");
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (!token) {
      toast.error("TOKEN DE RECUPERACIÓN NO VÁLIDO");
      return;
    }
    
    if (newPassword !== confirmPassword) {
      toast.error("LAS CONTRASEÑAS NO COINCIDEN");
      return;
    }
    
    if (newPassword.length < 6) {
      toast.error("LA CONTRASEÑA DEBE TENER AL MENOS 6 CARACTERES");
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, {
        token: token,
        new_password: newPassword
      });
      setResetComplete(true);
      toast.success("CONTRASEÑA RESTABLECIDA");
    } catch (error) {
      toast.error(error.response?.data?.detail || "ERROR AL RESTABLECER CONTRASEÑA");
    } finally {
      setLoading(false);
    }
  };

  // Only show reset form if we have a token from the URL (from email link)
  const showResetForm = !!token;

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <Navbar />

      <div className="max-w-md mx-auto px-4 py-20">
        <div className="bg-[#111] border border-zinc-800 p-8">
          {resetComplete ? (
            // Success state
            <div className="text-center">
              <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <Check className="w-8 h-8 text-green-400" />
              </div>
              <h1 className="text-2xl font-black text-white mb-4">CONTRASEÑA RESTABLECIDA</h1>
              <p className="text-zinc-500 font-mono text-sm mb-6">
                Ya puedes iniciar sesión con tu nueva contraseña
              </p>
              <Link to="/login">
                <Button className="btn-ruido bg-primary text-white font-mono">
                  INICIAR SESIÓN
                </Button>
              </Link>
            </div>
          ) : showResetForm ? (
            // Reset form (enter new password)
            <form onSubmit={handleResetPassword}>
              <div className="flex items-center gap-3 mb-6">
                <Key className="w-8 h-8 text-primary" />
                <h1 className="text-2xl font-black text-white">NUEVA CONTRASEÑA</h1>
              </div>
              
              <p className="text-zinc-500 font-mono text-sm mb-6">
                Introduce tu nueva contraseña
              </p>
              
              <div className="space-y-4">
                <div>
                  <Label className="font-mono text-xs text-zinc-500 mb-2 block">
                    NUEVA CONTRASEÑA
                  </Label>
                  <Input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="input-ruido"
                    placeholder="Mínimo 6 caracteres"
                    required
                  />
                </div>
                
                <div>
                  <Label className="font-mono text-xs text-zinc-500 mb-2 block">
                    CONFIRMAR CONTRASEÑA
                  </Label>
                  <Input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="input-ruido"
                    placeholder="Repite la contraseña"
                    required
                  />
                </div>
                
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full btn-ruido bg-primary text-white font-mono"
                >
                  {loading ? "RESTABLECIENDO..." : "RESTABLECER CONTRASEÑA"}
                </Button>
              </div>
            </form>
          ) : requestSent ? (
            // Request sent state - show appropriate message
            emailSentSuccess ? (
              <div className="text-center">
                <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Mail className="w-8 h-8 text-green-400" />
                </div>
                <h1 className="text-2xl font-black text-white mb-4">EMAIL ENVIADO</h1>
                <p className="text-zinc-500 font-mono text-sm mb-6">
                  Revisa tu bandeja de entrada (y spam) para encontrar el enlace de recuperación.
                </p>
                <p className="text-zinc-600 font-mono text-xs mb-6">
                  El enlace expirará en 1 hora.
                </p>
                <Link to="/login">
                  <Button variant="outline" className="btn-ruido border-zinc-700 font-mono">
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    VOLVER AL LOGIN
                  </Button>
                </Link>
              </div>
            ) : (
              <div className="text-center">
                <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Mail className="w-8 h-8 text-red-400" />
                </div>
                <h1 className="text-2xl font-black text-white mb-4">ERROR DE ENVÍO</h1>
                <p className="text-zinc-500 font-mono text-sm mb-4">
                  {emailError || "No se pudo enviar el email de recuperación."}
                </p>
                <p className="text-zinc-600 font-mono text-xs mb-6">
                  Contacta con el administrador en: <a href="mailto:privacidad@rockmaps.es" className="text-primary hover:underline">privacidad@rockmaps.es</a>
                </p>
                <Button 
                  variant="outline" 
                  className="btn-ruido border-zinc-700 font-mono"
                  onClick={() => { setRequestSent(false); setEmailError(""); }}
                >
                  INTENTAR DE NUEVO
                </Button>
              </div>
            )
          ) : (
            // Request form (enter email)
            <form onSubmit={handleRequestReset}>
              <div className="flex items-center gap-3 mb-6">
                <Key className="w-8 h-8 text-primary" />
                <h1 className="text-2xl font-black text-white">RECUPERAR CONTRASEÑA</h1>
              </div>
              
              <p className="text-zinc-500 font-mono text-sm mb-6">
                Introduce tu email y te enviaremos un enlace para restablecer tu contraseña
              </p>
              
              <div className="space-y-4">
                <div>
                  <Label className="font-mono text-xs text-zinc-500 mb-2 block">
                    EMAIL
                  </Label>
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="input-ruido"
                    placeholder="tu@email.com"
                    required
                  />
                </div>
                
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full btn-ruido bg-primary text-white font-mono"
                >
                  {loading ? "ENVIANDO..." : "ENVIAR ENLACE"}
                </Button>
                
                <Link to="/login" className="block text-center">
                  <span className="text-zinc-500 hover:text-primary font-mono text-sm">
                    <ArrowLeft className="w-3 h-3 inline mr-1" />
                    Volver al login
                  </span>
                </Link>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
